/*
index.jsx (for Detail component)
Adam Berger
July 2023
*/

import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import * as Constants from "components/Constants";
import DeleteModal from "components/DeleteModal";
import LoadingSpinner from "components/LoadingSpinner";
import MKTypography from "components/MKTypography";
import ModalWithAck from "components/ModalWithAck";
import Navbar from "components/Navbar";
import RecipeFooter from "components/RecipeFooter";
import RenderIngredients from "components/RenderIngredients";
import RenderInstructions from "components/RenderInstructions";
import ShareModal from "components/ShareModal";
import VariantModal from "components/VariantModal";
import { useEffect, useRef } from "react";
import { CustomButton } from "../../components/base/CustomButton";
import { UploadedPhotos } from "./UploadedPhotos";
import { parseUploadedImageUrls } from "./uploadedImageUrls";
import { useDetail } from "./useDetail";
import { FILTER_MAPPINGS } from "../../settings";

// import assets
import magicWand from "assets/icons/MagicWand.svg";
import pencil from "assets/icons/pencil.svg";
import pencilGrey from "assets/icons/PencilGrey.svg";
import share from "assets/icons/share.svg";
import shareGrey from "assets/icons/ShareGrey.svg";
import sliders from "assets/icons/sliders.svg";
import slidersGrey from "assets/icons/SlidersHorizontalGrey.svg";
import trash from "assets/icons/trash.svg";
import trashGrey from "assets/icons/TrashGrey.svg";
import utensils from "assets/icons/utensils.svg";
import "../../detail.css";

const WILL_MAKE_A_COPY_MESSAGE =
  "This recipe belongs to another Kasserole user. By editing, you will create your own copy of this recipe.";

const MAX_QTY_BADGES = 3;

const BADGE_LABELS = {
  lowCalorie: "Low Calorie",
  lowSodium: "Low Sodium",
  lowSugar: "Low Sugar",
  highFiber: "High Fiber",
  highProtein: "High Protein",
  lowFat: "Low Fat",
  lowCarbs: "Low Carb",
};

// Badge thresholds come from the browse filters, so a recipe carries a badge
// exactly when that filter would match it. A mapping like
// "ninfo_Dietary_Fiber>5" becomes { key: "dietary fiber", op: ">", limit: 5 },
// with the key matching parseNutritionalInfo's lowercased labels.
const nutritionBadges = Object.entries(FILTER_MAPPINGS).map(([name, expr]) => {
  const [, field, op, limit] = expr.match(/^(.+?)([<>])(\d+(?:\.\d+)?)$/);
  return {
    key: field.slice(Constants.NINFO_PREFIX.length).replace(/_/g, " ").toLowerCase(),
    op,
    limit: Number(limit),
    label: BADGE_LABELS[name],
  };
});

function earnedBadges(nutritionalAnalysis) {
  return nutritionBadges
    .filter(({ key, op, limit }) => {
      if (!(key in nutritionalAnalysis)) return false;
      const amount = Constants.nutrientAmount(nutritionalAnalysis[key]);
      if (amount === null) return false;
      return op === ">" ? amount > limit : amount < limit;
    })
    .map(({ label }) => label);
}

export const Detail = () => {
  const { user } = useAuth0();
  const {
    guid,
    setActionsDropdownIsActive,
    recipe,
    errorModalIsActive,
    errorTitle,
    errorMessage,
    setErrorModalIsActive,
    navigate,
    setVariantModalIsActive,
    setShareModalIsActive,
    shareRecipe,
    prepareToEdit,
    userOwnsThisRecipe,
    setDeleteModalIsActive,
    setDisclaimerModalIsActive,
    shareModalIsActive,
    deleteModalIsActive,
    variantModalIsActive,
    willCopyThisRecipeModalIsActive,
    setWillCopyThisRecipeModalIsActive,
    disclaimerModalIsActive,
    sharedFromModalIsActive,
    setSharedFromModalIsActive,
    generateSharedRecipeMessage,
  } = useDetail();

  // Check if user has god mode
  const isGodMode = user ? Constants.hasGodMode(Constants.encodeUser(user)) : false;

  // Ref + click-outside for the mobile dropdown in "Actions"
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Handler to check if user clicked outside dropdown
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setActionsDropdownIsActive(false); // close it
      }
    }
    // Add a global "mousedown" listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setActionsDropdownIsActive]);

  if (errorModalIsActive) {
    return (
      <>
        <Navbar />
        <ModalWithAck
          title={errorTitle}
          message={errorMessage}
          onClick={() => {
            setErrorModalIsActive(false);
            navigate(-1);
          }}
        />
      </>
    );
  }

  // if we're still loading the recipe, show a spinner
  if (!recipe || !recipe.recipe_name) {
    return <LoadingSpinner />;
  }


    // Build the recipe object. Ingredients and instructions are embedded in recipe.recipe_body; we parse them
  // out into their own strings. NutritionalAnalysis is extracted from the recipe.ninfo string. 
  // dxxxxrecipe.ninfoObj = createNinfoObj(recipe);
  // const nutritionalAnalysis = getNinfoObj(recipe.ninfo);
  
  const ingredients = Constants.extractIngredients(recipe.recipe_body);
  const instructions = Constants.extractInstructions(recipe.recipe_body);
  const notesContent = recipe.notes || 
    <span>
      No notes, but feel free to{" "}
      <span 
        onClick={() => prepareToEdit()} 
        style={{
          textDecoration: 'underline', 
          cursor: 'pointer',
          color: '#0d6efd',
          fontWeight: '600'
        }}
        className="edit-link"
      >
        edit
      </span>
      {" "}the recipe to add some!
    </span>;
  const nutritionalAnalysis = Constants.parseNutritionalInfo(recipe.ninfo)

  return (
    <>
      <Navbar />
      <div className="page_container_full">
        {/* HERO AREA */}
        <h2 className="detail-main-text-mobile">{recipe.recipe_name}</h2>
        <div className="hero-area">
          <div className="detail-img-container">
            {recipe.full_image_url ? (
              <img src={recipe.full_image_url} className="full_image_view" />
            ) : (
              <div className="full_image_view" />
            )}
            <div className="detail-img-overlay"></div>

            {/*
              1. Instead of pushing everything to the bottom, 
              split into a top and bottom container with 
              `justify-content: space-between`.
            */}
            <div className="detail-hero-content">
              {/* TOP container: Badges + Title */}
              <div className="detail-hero-top">
                <div className="detail-badge-container">
                  <div className="detail-hero-badge">
                    <img src={utensils} alt="category" />
                    {recipe.category[0].toUpperCase() + recipe.category.slice(1)}
                  </div>
                  {earnedBadges(nutritionalAnalysis)
                    .slice(0, MAX_QTY_BADGES)
                    .map((label) => (
                      <div key={label} className="detail-hero-badge">
                        {label}
                      </div>
                    ))}
                </div>

                <h2 className="detail-main-text">{recipe.recipe_name}</h2>
              </div>

              {/* BOTTOM container: Actions, Additional Buttons, etc. */}
              <div className="detail-hero-bottom">
                {/* Desktop and tablet button container */}
                <div className="detail-hero-btn-container">
                  <CustomButton
                    imgSrc={sliders}
                    imgSrcHover={slidersGrey}
                    text="Adapt"
                    className="detail-hero-btn detail-share-btn"
                    iconClassName="detail-share-icon"
                    onClickAction={() => setVariantModalIsActive(true)}
                  />
                  <CustomButton
                    imgSrc={share}
                    imgSrcHover={shareGrey}
                    text="Share"
                    className="detail-hero-btn detail-share-btn"
                    iconClassName="detail-share-icon"
                    onClickAction={shareRecipe}
                  />
                  <CustomButton
                    imgSrc={pencil}
                    imgSrcHover={pencilGrey}
                    text="Edit"
                    className="detail-hero-btn detail-share-btn"
                    iconClassName="detail-share-icon"
                    onClickAction={() => prepareToEdit(guid)}
                  />
                  {(userOwnsThisRecipe || isGodMode) && (
                    <CustomButton
                      imgSrc={trash}
                      imgSrcHover={trashGrey}
                      text="Delete"
                      className="detail-hero-btn detail-share-btn"
                      iconClassName="detail-share-icon"
                      onClickAction={() => setDeleteModalIsActive(true)}
                    />
                  )}
                </div>

                {/* Mobile button container */}
                <div className="detail-hero-btn-container-mobile">
                  <CustomButton
                    imgSrc={slidersGrey}
                    text="Adapt"
                    className="detail-hero-btn-mobile"
                    iconClassName="detail-icon-mobile"
                    onClickAction={() => setVariantModalIsActive(true)}
                  />
                  <CustomButton
                    imgSrc={shareGrey}
                    text="Share"
                    className="detail-hero-btn-mobile"
                    iconClassName="detail-icon-mobile"
                    onClickAction={shareRecipe}
                  />
                  <CustomButton
                    imgSrc={pencilGrey}
                    text="Edit"
                    className="detail-hero-btn-mobile"
                    iconClassName="detail-icon-mobile"
                    onClickAction={() => prepareToEdit(guid)}
                  />
                  {(userOwnsThisRecipe || isGodMode) && (
                    <CustomButton
                      imgSrc={trashGrey}
                      text="Delete"
                      className="detail-hero-btn-mobile"
                      iconClassName="detail-icon-mobile"
                      onClickAction={() => setDeleteModalIsActive(true)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* DESKTOP ONLY: Nutritional Data container */}
          <div className="nutritional-analysis-container-desktop">
            <div className="detail-right-title-container">
              <div className="detail-container-title">Nutritional Data</div>
              <CustomButton
                imgSrc={magicWand}
                text="Calculated using AI"
                className="detail-ai-badge"
                iconClassName=""
                onClickAction={() => setDisclaimerModalIsActive(true)}
              />
            </div>
            <MKTypography variant="caption">
              <ul className="preserveformatting detail-right-body-text">

                {Object.entries(nutritionalAnalysis).map(([key, data], i) => (
                  <li key={i}>
                    <span style={{ fontWeight: "bold" }}>{key}:</span> {data}
                  </li>
                ))}

              </ul>
            </MKTypography>
          </div>
        </div>
        {/* END HERO AREA */}

        {/* MAIN CONTENT */}
        <div className="detail-content-container">
          <div className="detail-left-container">
            <div className="detail-left-title-container">
              <div className="detail-container-title">Recipe</div>
              {recipe.bguai === "True" && (
                <CustomButton
                  imgSrc={magicWand}
                  text="Made With AI"
                  className="detail-ai-badge"
                  iconClassName=""
                  onClickAction={() => setDisclaimerModalIsActive(true)}
                />
              )}
            </div>

            <div className="preserveformatting detail-left-body-text">
              <label className="input-label">Ingredients:</label>
              <RenderIngredients ingredients={ingredients} />
              <br/>
              <label className="input-label">Instructions:</label>
              <RenderInstructions instructions={instructions} />

              <div className="preserveformatting detail-left-body-text">
                <label className="input-label">Notes:</label>
                {notesContent}
              </div>

            </div>

            <div className="detail-left-footer-container">
              <UploadedPhotos
                key={guid}
                urls={parseUploadedImageUrls(recipe)}
              />
              <RecipeFooter
                sourceUrl={recipe.source_url}
                tse={recipe.tse}
                vc={recipe.vc}
                sharer={recipe.sf}
                userOwnsThisRecipe={userOwnsThisRecipe}
                isLoggedIn={!!user}
                ownerNickname={recipe.nickname}
              />
              <div className="detail-left-footer-views"></div>
              <div className="detail-left-footer-date"></div>
            </div>
          </div>

          <div className="detail-right-container">
            <div className="detail-right-title-container">
              <div className="detail-container-title">Nutritional Data</div>
              <CustomButton
                imgSrc={magicWand}
                text="Calculated using AI"
                className="detail-ai-badge"
                iconClassName=""
                onClickAction={() => setDisclaimerModalIsActive(true)}
              />
            </div>
            <MKTypography variant="caption">
              <ul className="preserveformatting detail-right-body-text">

                {Object.keys(nutritionalAnalysis).map((key, index) => (
                  <li key={index}>
                    <span>{key.replace("_", " ")}:</span>{" "}
                    <span>{nutritionalAnalysis[key]}</span>
                  </li>
                ))}

              </ul>
            </MKTypography>
            <div className="detail-right-footer-container">
              <UploadedPhotos
                key={guid}
                urls={parseUploadedImageUrls(recipe)}
              />
              <RecipeFooter
                sourceUrl={recipe.source_url}
                tse={recipe.tse}
                vc={recipe.vc}
                sharer={recipe.sf}
                userOwnsThisRecipe={userOwnsThisRecipe}
                isLoggedIn={!!user}
                ownerNickname={recipe.nickname}
              />
              <div className="detail-left-footer-views"></div>
              <div className="detail-left-footer-date"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {renderModal(
        ShareModal,
        shareModalIsActive,
        () => setShareModalIsActive(!shareModalIsActive),
        { guid, recipeName: recipe.recipe_name }
      )}
      {renderModal(
        DeleteModal,
        deleteModalIsActive,
        () => setDeleteModalIsActive(!deleteModalIsActive),
        { guid }
      )}
      {renderModal(
        VariantModal,
        variantModalIsActive,
        () => setVariantModalIsActive(!variantModalIsActive),
        { guid }
      )}
      {renderModal(
        ModalWithAck,
        willCopyThisRecipeModalIsActive,
        () => setWillCopyThisRecipeModalIsActive(!willCopyThisRecipeModalIsActive),
        {
          title: "Create a Copy",
          copy: true,
          message: WILL_MAKE_A_COPY_MESSAGE,
          onClick: () => setWillCopyThisRecipeModalIsActive(false),
        }
      )}
      {renderModal(
        ModalWithAck,
        disclaimerModalIsActive,
        () => setDisclaimerModalIsActive(!disclaimerModalIsActive),
        {
          title: "AI-Generated Content",
          message: Constants.AI_DISCLAIMER_MESSAGE,
        }
      )}
      {renderModal(
        ModalWithAck,
        sharedFromModalIsActive,
        () => setSharedFromModalIsActive(!sharedFromModalIsActive),
        {
          title: "Shared Recipe",
          message: generateSharedRecipeMessage(recipe.sf),
        }
      )}
    </>
  );
};

const renderModal = (ModalComponent, isActive, toggleFunction, additionalProps) => {
  return (
    isActive && (
      <ModalComponent
        toggle={toggleFunction}
        backdrop={true}
        onClick={toggleFunction}
        {...additionalProps}
      />
    )
  );
};
